/**
 * SP 复投子账户 Wallet ORB — GET /api/admin/stats/sp-reinvest-wallet-orb
 */
import { useMemo, useState } from 'react';
import { Alert, Card, Col, Input, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';

type ReinvestCoin = 'SP1' | 'SP2';

interface WalletOrbTotals {
  accounts: number;
  wallet_orb_sum: string;
  account_family_wallet_orb_sum: string;
  account_family_count: number;
  account_family_member_count: number;
  bot_account_count: number;
  bot_wallet_orb_sum: string;
  wallet_uo_col_sum: string;
  accounts_with_orb: number;
  avg_wallet_orb: string;
  avg_wallet_orb_positive: string;
  unique_masters: number;
}

interface WalletOrbBreakdown extends WalletOrbTotals {
  reinvest_coin: ReinvestCoin;
}

interface WalletOrbRow {
  sub_id: string;
  sub_account?: string;
  account_master_id?: string | null;
  account_master_account?: string | null;
  master_id: string;
  master_account?: string;
  reinvest_coin: ReinvestCoin;
  exit_mult?: string;
  sub_cap_usd?: string;
  sub_used_static?: string;
  master_cap_usd?: string;
  master_used_static?: string;
  pay_type?: string;
  split2?: string;
  create_time?: string;
  wallet_orb: string;
  account_family_wallet_orb?: string;
  account_family_member_count?: number;
  bot_account_count?: number;
  bot_wallet_orb_sum?: string;
  wallet_uo_col: string;
}

interface WalletOrbSummary {
  totals: WalletOrbTotals;
  by_reinvest_coin: WalletOrbBreakdown[];
  items: WalletOrbRow[];
  criteria: string[];
}

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

function fmt(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? numberFormatter.format(n) : String(value);
}

function phpUserLink(kind: 'master' | 'sub', accountOrId?: string): string {
  const q = encodeURIComponent(accountOrId ?? '');
  return `/parity/user_user?kind=${kind}&q=${q}`;
}

function subAccountTag(account?: string): string {
  const suffix = account?.match(/-(\d+)$/)?.[1];
  if (!suffix) return '子账户';
  return `子${Number(suffix)}`;
}

export function SpReinvestWalletOrbPage() {
  const [keyword, setKeyword] = useState('');
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'stats', 'sp-reinvest-wallet-orb'],
    queryFn: () => api.get<WalletOrbSummary>('/api/admin/stats/sp-reinvest-wallet-orb'),
  });

  const totals = data?.totals;
  const breakdownRows = data?.by_reinvest_coin ?? [];
  const itemRows = data?.items ?? [];
  const filteredItems = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return itemRows;
    return itemRows.filter((row) =>
      [
        row.sub_id,
        row.sub_account,
        row.account_master_id,
        row.account_master_account,
        row.master_id,
        row.master_account,
        row.reinvest_coin,
      ].some((value) => String(value ?? '').toLowerCase().includes(q)),
    );
  }, [itemRows, keyword]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>复投子账户 ORB 统计</h2>
      <Typography.Paragraph type="secondary">
        统计「SP1/SP2 复投开子、无现金充值、主账户已出局」名单持有的 Wallet ORB。
        口径为实时读取 PHP 镜像计算，不读取本地 CSV。
      </Typography.Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Wallet ORB 计算口径"
        description={
          <Typography.Text>
            FLOOR(raw_uo + open_otc_remaining × 7/8)，其中 raw_uo 来自{' '}
            <Typography.Text code>php_user_user._raw-&gt;&gt;'uo'</Typography.Text>，open OTC
            来自未完成挂卖剩余额度。
          </Typography.Text>
        }
      />

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic
              title="Wallet ORB 合计（含子）"
              value={fmt(totals?.account_family_wallet_orb_sum ?? '0')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="名单账户数" value={totals?.accounts ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="ORB > 0 账户" value={totals?.accounts_with_orb ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="账号主号数（去重）" value={totals?.account_family_count ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="含子账户数" value={totals?.account_family_member_count ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="虚拟账户数" value={totals?.bot_account_count ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="虚拟账户 ORB" value={fmt(totals?.bot_wallet_orb_sum ?? '0')} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic
              title="自身 ORB 合计（参考）"
              value={fmt(totals?.wallet_orb_sum ?? '0')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={isLoading}>
            <Statistic title="raw uo 列合计（参考）" value={fmt(totals?.wallet_uo_col_sum ?? '0')} />
          </Card>
        </Col>
      </Row>

      <Card title="SP1 / SP2 拆分" style={{ marginTop: 16 }} loading={isLoading}>
        <Table
          rowKey="reinvest_coin"
          size="small"
          loading={isFetching}
          pagination={false}
          dataSource={breakdownRows}
          columns={[
            {
              title: '类型',
              dataIndex: 'reinvest_coin',
              render: (v: ReinvestCoin) => <Tag color={v === 'SP1' ? 'blue' : 'purple'}>{v}</Tag>,
            },
            { title: '名单账户数', dataIndex: 'accounts', align: 'right' },
            {
              title: 'Wallet ORB（含子）',
              dataIndex: 'account_family_wallet_orb_sum',
              align: 'right',
              render: fmt,
            },
            {
              title: '自身 ORB（参考）',
              dataIndex: 'wallet_orb_sum',
              align: 'right',
              render: fmt,
            },
            {
              title: 'raw uo 列（参考）',
              dataIndex: 'wallet_uo_col_sum',
              align: 'right',
              render: fmt,
            },
            { title: 'ORB > 0', dataIndex: 'accounts_with_orb', align: 'right' },
            { title: '账号主号数', dataIndex: 'account_family_count', align: 'right' },
            { title: '含子账户数', dataIndex: 'account_family_member_count', align: 'right' },
            { title: '虚拟账户数', dataIndex: 'bot_account_count', align: 'right' },
            {
              title: '虚拟账户 ORB',
              dataIndex: 'bot_wallet_orb_sum',
              align: 'right',
              render: fmt,
            },
          ]}
        />
      </Card>

      <Card
        title="筛选账户明细"
        style={{ marginTop: 16 }}
        extra={
          <Input.Search
            allowClear
            placeholder="搜索子账户 / 主号 / 来源 / ID"
            style={{ width: 260 }}
            onChange={(e) => setKeyword(e.target.value)}
          />
        }
      >
        <Table
          rowKey="sub_id"
          size="small"
          loading={isFetching}
          dataSource={filteredItems}
          scroll={{ x: 1740 }}
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (n) => `共 ${n} 条` }}
          columns={[
            {
              title: '类型',
              dataIndex: 'reinvest_coin',
              width: 86,
              filters: [
                { text: 'SP1', value: 'SP1' },
                { text: 'SP2', value: 'SP2' },
              ],
              onFilter: (value, row) => row.reinvest_coin === value,
              render: (v: ReinvestCoin) => <Tag color={v === 'SP1' ? 'blue' : 'purple'}>{v}</Tag>,
            },
            {
              title: '子账户',
              dataIndex: 'sub_account',
              width: 170,
              render: (v: string | undefined, row: WalletOrbRow) => (
                <div>
                  <Space size={4}>
                    <Link to={phpUserLink('sub', v || row.sub_id)}>{v || '-'}</Link>
                    <Tag color="geekblue">{subAccountTag(v)}</Tag>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ID {row.sub_id}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: '账号主号',
              dataIndex: 'account_master_account',
              width: 170,
              render: (v: string | undefined, row: WalletOrbRow) => (
                <div>
                  <Space size={4}>
                    {row.account_master_id ? (
                      <Link to={phpUserLink('master', v || row.account_master_id)}>
                        {v || '-'}
                      </Link>
                    ) : (
                      <Typography.Text>{v || '-'}</Typography.Text>
                    )}
                    <Tag color={row.account_master_id ? 'blue' : 'orange'}>
                      {row.account_master_id ? '主账户' : '主号未找到'}
                    </Tag>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {row.account_master_id ? `ID ${row.account_master_id}` : 'PHP 镜像无该主号行'}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: '来源账户(fid)',
              dataIndex: 'master_account',
              width: 170,
              render: (v: string | undefined, row: WalletOrbRow) => (
                <div>
                  <Space size={4}>
                    <Link to={phpUserLink('master', v || row.master_id)}>{v || '-'}</Link>
                    <Tag>fid</Tag>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ID {row.master_id}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: 'Wallet ORB（含子）',
              dataIndex: 'account_family_wallet_orb',
              align: 'right',
              width: 130,
              sorter: (a, b) =>
                Number(a.account_family_wallet_orb ?? a.wallet_orb) -
                Number(b.account_family_wallet_orb ?? b.wallet_orb),
              defaultSortOrder: 'descend',
              render: (v: string | undefined, row: WalletOrbRow) => fmt(v ?? row.wallet_orb),
            },
            {
              title: '自身 ORB',
              dataIndex: 'wallet_orb',
              align: 'right',
              width: 110,
              sorter: (a, b) => Number(a.wallet_orb) - Number(b.wallet_orb),
              render: fmt,
            },
            {
              title: '含子数',
              dataIndex: 'account_family_member_count',
              align: 'right',
              width: 90,
              render: (v: number | undefined) => v ?? 1,
            },
            {
              title: '虚拟数',
              dataIndex: 'bot_account_count',
              align: 'right',
              width: 90,
              render: (v: number | undefined) => v ?? 0,
            },
            {
              title: '虚拟 ORB',
              dataIndex: 'bot_wallet_orb_sum',
              align: 'right',
              width: 110,
              sorter: (a, b) => Number(a.bot_wallet_orb_sum ?? 0) - Number(b.bot_wallet_orb_sum ?? 0),
              render: (v: string | undefined) => fmt(v ?? '0'),
            },
            {
              title: 'raw uo 列',
              dataIndex: 'wallet_uo_col',
              align: 'right',
              width: 130,
              sorter: (a, b) => Number(a.wallet_uo_col) - Number(b.wallet_uo_col),
              render: fmt,
            },
            {
              title: '倍数',
              dataIndex: 'exit_mult',
              align: 'right',
              width: 80,
            },
            {
              title: '子账户静态',
              dataIndex: 'sub_used_static',
              align: 'right',
              width: 140,
              render: (v: string | undefined, row: WalletOrbRow) =>
                `${fmt(v ?? '0')} / ${fmt(row.sub_cap_usd ?? '0')}`,
            },
            {
              title: '主账户静态',
              dataIndex: 'master_used_static',
              align: 'right',
              width: 140,
              render: (v: string | undefined, row: WalletOrbRow) =>
                `${fmt(v ?? '0')} / ${fmt(row.master_cap_usd ?? '0')}`,
            },
            {
              title: 'pay_type',
              dataIndex: 'pay_type',
              width: 90,
            },
            {
              title: 'split2',
              dataIndex: 'split2',
              width: 80,
            },
            {
              title: '开通时间',
              dataIndex: 'create_time',
              width: 170,
              render: (v: string | undefined) => v?.slice(0, 19) ?? '-',
            },
          ]}
        />
      </Card>

      <Card title="筛选条件" style={{ marginTop: 16 }}>
        <Space direction="vertical" size={6}>
          {(data?.criteria ?? []).map((item) => (
            <Typography.Text key={item}>{item}</Typography.Text>
          ))}
        </Space>
      </Card>
    </div>
  );
}
